import { useEffect, useMemo, useState } from 'react';
import type { CSSObjectWithLabel } from 'react-select';

import { useGetAllClassesWithSearchInfinite } from '~entities/classes/api/get-all-classes-with-search';

import {
  SELECT_MENU_PORTAL_CLASS,
  defaultClassNames,
  defaultStyles,
} from '~shared/components/select/helper';
import Select from '~shared/components/select/select';
import { useDebounce } from '~shared/shadcn/expansions/multiple-selector';
import { cn } from '~shared/shadcn/lib/utils';

export interface ClassOption {
  label: string;
  value: string;
}

export interface ClassSelectorProps {
  selectedSessionId: string | undefined;
  selectedClass:
    | {
        label: string;
        value: string;
      }
    | undefined
    | null;
  onChange: (
    value:
      | {
          label: string;
          value: string;
        }
      | undefined
      | null,
  ) => void;
  /** When true, classes in ended sessions are included (admin enrollment in past/archived sessions) */
  showPastClasses?: boolean;
  /**
   * Portal the menu (required inside Radix dialogs so the menu is not clipped).
   * Prefer `menuPortalTarget` set to the DialogContent element.
   * Pass `null` while the dialog ref is resolving — do not default to body.
   */
  menuInPortal?: boolean;
  /** Element to portal into. Defaults to document.body only when omitted. */
  menuPortalTarget?: HTMLElement | null;
}

export const ClassSelector = ({
  selectedSessionId,
  selectedClass,
  onChange,
  showPastClasses = false,
  menuInPortal = false,
  menuPortalTarget = null,
}: ClassSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState(selectedClass?.label ?? '');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (selectedClass == null) {
      setSearchQuery('');
    }
  }, [selectedClass]);

  const {
    data,
    isFetching,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllClassesWithSearchInfinite({
    sessionId: selectedSessionId,
    name: debouncedSearchQuery,
    // Enrollment should match All Classes with Past/Archived off: active
    // classes only. showPastClasses alone means past-only on the API.
    showPastClasses: showPastClasses === true ? true : undefined,
    enforceStatusFilters: showPastClasses === true ? undefined : true,
    limit: 100,
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const options = useMemo(() => {
    const pages = data?.pages ?? [];
    const seen = new Set<string>();
    const classes = [];
    for (const page of pages) {
      for (const c of page.data.classes) {
        if (!seen.has(c._id)) {
          seen.add(c._id);
          classes.push(c);
        }
      }
    }
    let foundSession = false;
    const optionsFromData = classes.map((s) => {
      if (s._id === selectedClass?.value) {
        foundSession = true;
      }
      return { label: s.title, value: s._id };
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (selectedClass != null && !foundSession) {
      optionsFromData.push({
        label: selectedClass.label,
        value: selectedClass.value,
      });
    }

    return optionsFromData;
  }, [data?.pages, selectedClass?.label, selectedClass?.value]);

  const currentOption = useMemo(
    () =>
      selectedClass
        ? { label: selectedClass.label || '', value: selectedClass.value || '' }
        : undefined,
    [selectedClass?.label, selectedClass?.value],
  );

  const resolvedPortalTarget = menuInPortal
    ? // Explicit null = caller is still resolving a DialogContent ref; do not
      // fall back to body (that reintroduces Radix outside-click dismiss).
      menuPortalTarget === undefined
      ? typeof document !== 'undefined'
        ? document.body
        : null
      : menuPortalTarget
    : null;

  const portalStyles = menuInPortal
    ? {
        ...defaultStyles,
        menuPortal: (base: CSSObjectWithLabel) => ({
          ...base,
          zIndex: 70,
        }),
      }
    : undefined;

  return (
    <Select
      isClearable
      value={currentOption}
      onChange={(option) => {
        const opt = option as ClassOption | null | undefined;
        onChange(opt);
      }}
      placeholder='Select Class'
      options={options}
      isLoading={isPending || (isFetching && !isFetchingNextPage)}
      isMenuListFetchingMore={isFetchingNextPage}
      onMenuScrollToBottom={() => {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      }}
      onInputChange={(inputValue, { action }) => {
        if (action === 'input-change') {
          setSearchQuery(inputValue);
        }
      }}
      styles={portalStyles}
      menuPosition={menuInPortal ? 'fixed' : undefined}
      menuPortalTarget={resolvedPortalTarget}
      classNames={
        menuInPortal
          ? {
              ...defaultClassNames,
              menuPortal: (state) =>
                cn(
                  defaultClassNames.menuPortal?.(state),
                  SELECT_MENU_PORTAL_CLASS,
                ),
            }
          : undefined
      }
    />
  );
};
